// @ts-check
import Debug from 'debug'
const debug = Debug('app:workforce:controller')
import selectCrewCtrl from './select-crew/selectCrew.ctrl'
import selectCrewTpl from './select-crew/selectCrew.html'
import dsbCtrl from './rate/dsb.ctrl'
import dsbTpl from './rate/dsb.html'

import { empty } from '@bluecrew/bluecrew-lib'
import {not, prop, pipe, pluck, join, filter, propEq} from 'ramda'
import groupMessageController from './message/groupMessage.ctrl'
import groupMessage from './message/groupMessage.html'

const workerStatuses = {
  'SUSPENDED': {class: 'warning', description: 'Suspended'},
  'ELIGIBLE': {class: 'success', description: 'Eligible'},
  'FIRED': {class: 'danger', description: 'Fired'},
  'FAILED_INTERVIEW': {class: 'danger', description: 'Failed Interview'},
  'FOLLOW_UP_INTERVIEW': {class: 'primary', description: 'Needs Interview'},
  'PASSED_INTERVIEW': {class: 'primary', description: 'Passed Interview'},
  'STARTED_SIGN_UP': {class: 'default', description: 'Signed Up'},
  'PENDING': {class: 'rose', description: 'Pending'},
  'ENTERED_GENERAL_INFO': {class: 'rose', description: 'Entered General Info'},
  'ENTERED_INTERESTS': {class: 'rose', description: 'Entered Interests'},
  'SIGNED_CONTRACT': {class: 'rose', description: 'Sign Contract'},
  'ENTERED_BACKGROUND_CHECK_INFO': {class: 'rose', description: 'Entered Background Check'},
  'RUNNING_BACKGROUND_CHECK': {class: 'passive', description: 'Running Checkr'},
  'ELIGIBLE_EOR': {class: 'passive', description: 'EOR'},
  'ENTERED_TAX_INFO': {class: 'passive', description: 'Entered Tax Info'},
  'NO_SHOW_INTERVIEW': {class: 'dark', description: 'No Show Interview'},
  'DOUBLE_PROFILE': {class: 'dark', description: 'Duplicate Account'},
  'PAUSED_PROFILE': {class: 'dark', description: 'Paused'},
  'NEEDS_I9': {class: 'primary', description: 'Needs I9'},
  'FAILED_BACKGROUND_CHECK': {class: 'danger', description: 'Failed Background'},
  'REGION_WAITLIST': {class: 'rose', description: 'Out of Region Signup'},
  'NEEDS_CREWMEMBER_I9': {class: 'primary', description: 'Agent Selection'},
  'NEEDS_I9_AGENT': {class: 'primary', description: 'Needs I9 Agent'},
  'INACTIVE': {class: 'dark', description: 'Inactive'},
}

const TabNumToNameMap = Object.freeze({
  0: 'All',
  1: 'Unrated',
  2: 'Favorites',
  3: 'Groups',
  4: 'DSB'   
});

/** @typedef {object} Worker
 * @property {string} username
 * @property {string} external_id
 * @property {string} firstName
 * @property {string} lastName
 * @property {string} phoneNumber
 * @property {number} points
 * @property {null|string} profilePic
 * @property {number} approvalRating
 * @property {number} ratingId
 * @property {number} user_id
 * @property {number} favorite
 * @property {number|null} dont_sendor
 * @property {string|null} lastShift
 * @property {number|null} shiftsWorked
 * @property {number|null} total_shift_count
 * @property {number} neutral_count
 * @property {number} negative_count
 * @property {string} avatarColor
 * @property {boolean} selected
 */

const getNonDSBWorkers = (workforce) => filter(propEq('dont_sendor', null), (workforce))

export class WorkforceController {
  /* @ngInject */
  constructor ($scope, $state, $mdToast, $filter, Users, Crews, Jobs, myCrews, $mdMedia, $mdDialog, $templateCache, $mdColorPalette, regions, $window, $q) {
    this.showLoaderCircle = true
    this.$mdMedia = $mdMedia
    this.$mdToast = $mdToast
    this.$scope = $scope
    this.Users = Users
    this.Crews = Crews
    this.Jobs = Jobs
    this.$state = $state
    this.regions = regions
    this.$window = $window
    this.$filter = $filter
    this.$templateCache = $templateCache
    this.$mdColorPalette = $mdColorPalette
    this.$q = $q
    this.selectedPlace = ''
    this.searchLocationText = ''
    this.$mdDialog = $mdDialog
    this.filter = {}
    this.workerStatuses = workerStatuses
    this.workforce = []
    this.hitsPerPage = 10
    this.fabOpen = false
    this.tooltips = {
      unrated: 'Smart Tip: Here you can manage all previous workers organized by groups. Make sure to rate all workers to ensure future jobs are filled with the best Crew!',
      directHire: 'Request specific individual workers for an upcoming job.',
      workerProfile: 'Learn more about your workers by checking out their bios, activity, and notes that your company has provided.'
    }
    // fab toolbar closes if it's opened and the user clicks anywhere. this
    // gets around that fact.
    this.$scope.$watch('wf.fabOpen', () => {
      this.fabOpen = this.isFabOpen()
    })
    // These are not cloned copies. They contain filtered arrays with
    // references to the original objects in the workforce array.
    if (this.$state.includes('app.workforce.worker') || this.$state.includes('app.workforce')) {
      this.getMyWorkforce()
      .then((workforce) => {
        this.workforce = workforce
        this.$scope.$watch('wf.tab', this.tabChanged())
        this.$scope.$on('deselectAll', this.tabChanged())
        this.crews = myCrews
        this.unrated = this.Users.getUnrated()
        this.favorites = this.Users.getFavorites()
        this.nosends = this.Users.getNosends()
        this.currentForce = getNonDSBWorkers(this.workforce)
        $scope.$emit('app:skipLoader')
        if (this.workforce.length) {
          $state.go('app.workforce.worker', {id: this.workforce[0].external_id})
        }
        this.showLoaderCircle = false
      })
    } else if (this.$state.includes('app.viewprofiles')) {
      $scope.$emit('app:skipLoader')
      this.UserSearch = Users.userSearch(this.hitsPerPage)
      this.limit = {}
      this.page = ''
      this.hits = []
      this.query = ''
      this.facets = {}
      this.numericFacets = [
        'shifts_worked',
        'favorites',
        'ncns_count',
        'dsb_count',
        'short_notice_cancellations'
      ]
      this.dateFacets = [
        'last_session',
        'last_worked_shift',
        'eligible_date'
      ]
      this.searchRadius = 24000
      this.facetHeaders = this.UserSearch.state.facets
      this.UserSearch.on('result', results => {
        this.hits = results.hits
        this.page = '' + (results.page + 1)
        this.totalPages = results.nbPages
        this.segmentSize = results.nbHits
        this.facetHeaders.forEach((facet) => {
          let facetValues = results.getFacetValues(facet)
          if (facet != 'region') {
            facetValues = facetValues.slice(0, 10)
          }
          this.facets[facet] = facetValues
          this.limit[facet] = 10
        })

        this.showLoaderCircle = false
      })
      if (this.AuthService) {
        this.toggleFacet(facet.name, 'region')
      } else {
        this.UserSearch.setQuery('').search()
      }
    } else {
      $scope.$emit('app:skipLoader')
      $state.go('app.applicants.applicant', {id: this.workforce[0].external_id})
      this.$scope.$on('moderateApp', this.updateWorkforce(this.applicantRoute))
      this.showLoaderCircle = false
    }

    this.selectedWorkers = []
  }

  getMyWorkforce () {
    return this.Users.getWorkforce()
      .catch((err) => {
        debug(err)
        this.$mdDialog.show(
          this.$mdDialog.alert()
          .clickOutsideToClose(true)
          .title('Server Error')
          .textContent('There was an error fetching your workforce. Please try again later.')
          .ariaLabel('Server Error')
          .ok('OK')
          ).then(this.$state.go('login'))
      })
  }

  isFabOpen () {
    return this.getSelected(this.applicantRoute) > 0
  }

  selectCrew ($ev) {
    const crews = this.crews
    this.resetWorkerSelection()

    return this.$mdDialog.show({
      controller: selectCrewCtrl,
      controllerAs: 'wcr',
      template: selectCrewTpl,
      targetEvent: $ev,
      clickOutsideToClose: true,
      locals: {
        crews
      }
    })
  }

  sortByRegion (region) {
    this.Users.fetchProfiles(region)
      .then(profiles => {
        this.workforce = profiles
      })
  }

  updateWorkforce () {
    if (this.applicantRoute) {
      this.Users.fetchProfiles()
        .then(profiles => {
          this.workforce = profiles
        })
    } else {
      this.Users.getWorkforce()
        .then(wf => {
          this.workforce = wf
          this.nosends = this.Users.getNosends()
          this.currentForce = getNonDSBWorkers(this.workforce)
          this.unrated = this.Users.getUnrated()
          this.favorites = this.Users.getFavorites()
        })
    }
  }

  tabChanged (newValue, oldValue) {
    return () => {
      this.resetWorkerSelection()
    }
  }

  // Resets all selected workers and the allSelect flag
  resetWorkerSelection () {
    this.Users.deselectAll(this.applicantRoute)
    this.allSelect = false
    this.workforce.forEach(x => { x.selected = false })
    this.fabOpen = false
  }

  /**
   * @param {Worker[]} base
   * @param {Worker[]} filter
   * Return an array of items that `base` and `filter` share
   */
  filterWorkersWith(base, filter) {
    const filterWorkerIds = filter.map((item) => item.external_id)
    return base.filter((w) => filterWorkerIds.includes(w.external_id))
  }

  /**
   * @param {Worker[]} base
   * @param {Worker[]} remove
   * Return an array of items in `base` that don't exist in `remove`
   */
  removeWorkersWith(base, remove) {
    const filterWorkerIds = remove.map((item) => item.external_id)
    return base.filter((w) => !filterWorkerIds.includes(w.external_id))
  }

  /**
   * @param {Worker[]} workers
   * @param {number} tab
   * Filters down a worker array with a tab type
   */
  filterSelectedWorkersByTab(workers, tab) {
    let filteredWorkers = workers
    if (TabNumToNameMap[tab] === "All") {
      filteredWorkers = this.removeWorkersWith(workers, this.nosends)
    } else if (TabNumToNameMap[tab] === "Unrated") {
      filteredWorkers = this.filterWorkersWith(workers, this.unrated)
    } else if (TabNumToNameMap[tab] === "Favorites") {
      filteredWorkers = this.filterWorkersWith(workers, this.favorites)
    } else if (TabNumToNameMap[tab] === "Groups") {
      filteredWorkers = this.filterWorkersWith(workers, this.Crews.getSelectedCrew().users)
    } else if (TabNumToNameMap[tab] === "DSB") {
      filteredWorkers = []
    }
    return filteredWorkers
  }

  /**
   * @param {Worker[]} workers
   * @param {number} tab
   */
  selectAll (workers, tab) {
    if (this.allSelect) {
      if (this.applicantRoute) {
        this.Users.selectAllProfiles(workers)
      } else {
        const filteredWorkers = this.filterSelectedWorkersByTab(workers, tab) 
        this.Users.selectAll(filteredWorkers)
      }
    } else {
      this.Users.deselectAll(this.applicantRoute)
    }
    this.fabOpen = this.allSelect === true
  }

  select (worker) {
    this.fabOpen = not(empty(this.getSelected(this.applicantRoute)))
  }

  toast (content) {
    this.$scope.$parent.app.showSimpleToast(content)
  }

  details (worker) {
    debug(worker)
    let workerRoute = 'app.workforce.worker'
    let workerMobileRoute = 'app.worker'
    if (this.$state.includes('app.workforce.worker') || this.$state.includes('app.workforce')) {
      workerRoute = 'app.workforce.worker'
      workerMobileRoute = 'app.worker'
    } else {
      workerRoute = 'app.applicants.applicant'
      workerMobileRoute = 'app.applicant'
    }
    if (this.$mdMedia('sm') || this.$mdMedia('xs')) {
      this.$scope.$emit('app:skipLoader')
      this.$state.go(workerMobileRoute, {
        id: worker.external_id
      })
    } else {
      this.$scope.$emit('app:skipLoader')
      this.$state.go(workerRoute, {
        id: worker.external_id
      })
    }
  }

  dontSend ($ev, worker) {
    const confirm = {
      controller: dsbCtrl,
      controllerAs: 'dsb',
      template: dsbTpl,
      fullscreen: true,
      targetEvent: $ev,
      clickOutsideToClose: true,
      locals: {
        worker,
        job: null,
        schedule: null,
        ratingType: null
      }
    }

    return this.$mdDialog.show(confirm)
      .then((results) => {
        this.Users.dontSend(worker.external_id, results.reason, results.dsbRating, results.categoryId)
      })
      .then(this.Users.removeWorker(worker.external_id))
      .then(this.dontSendSuccess.bind(this))
      .catch(this.dontSendError.bind(this))
  }

  dontSendSuccess () {
    this.updateWorkforce()
    this.$mdToast.showSimple('Marked user as do not send.')
  }

  dontSendError (err) {
    if (!err) return
    console.error(err)
    this.$mdToast.showSimple('Unable to mark user as do not send.')
  }

  favError (err) {
    debug(err)
    this.$mdToast.showSimple('Unable to favorite user.')
  }

  unfavError (err) {
    debug(err)
    this.$mdToast.showSimple('Unable to remove user from favorites.')
  }

  getSelected () {
    return this.Users.getSelected(this.applicantRoute).length
  }

  rate (rating) {
    let userIds = this.Users.getSelected(this.applicantRoute)
    this.Users.rate(rating, userIds)
      .then(() => {
        let word = (userIds.length > 1) ? 'users' : 'user'
        const msg = `${userIds.length} ${word} rated!`
        this.toast(msg)
        this.$scope.$broadcast('deselectAll')
        this.updateWorkforce()
      })
      .catch((err) => {
        if (!err) return
        debug(err)
        this.toast('Unable to post rating.')
      })
  }
  searchSendJobId (jobId) {
    if (!jobId) {
      return
    }

    this.sendJobId = jobId
  }

  selectedSendJobId (job) {
    if (!job) {
      return
    }

    this.sendJobId = job.job_id.toString()
  }

  sendJob (jobId) {
    let userIds = this.Users.getSelected(this.applicantRoute)
    this.Users.sendJob(userIds, jobId)
      .then(() => {
        let word = (userIds.length > 1) ? 'workers' : 'worker'
        const msg = `${userIds.length} ${word} Sent Job`
        this.$scope.$broadcast('deselectAll')
        this.toast(msg)
      })
      .catch((err) => {
        if (!err) return
        debug(err)
        this.toast('Unable to send job')
      })
  }

  putOnJob (jobId) {
    let userIds = this.Users.getSelected(this.applicantRoute)
    this.Users.checkOverlap({userIds, jobId})
      .then(null, this.handleOverlap.bind(this))
      .then(() => this.Users.putOnJob(userIds, jobId))
      .then(() => {
        let word = (userIds.length > 1) ? 'workers' : 'worker'
        const msg = `${userIds.length} ${word} Put On Job`
        this.$scope.$broadcast('deselectAll')
        this.toast(msg)
      })
      .catch((err) => {
        if (!err) return
        console.log(err)
        this.toast('Unable to Put user On job')
      })
  }

   // If people are on jobs and you try to put them on overlapping jobs
  handleOverlap (e) {
    if (!e.status || e.status !== 409) throw e
    let text
    let title

    const users = pipe(
      prop('reason'),
      JSON.parse,
      pluck('name')
    )(e.data)

    title = 'The following workers have conflicting schedules. Continue posting?'
    text = join(', ', users)

    const confirm = this.$mdDialog.confirm()
      .title(title)
      .textContent(text)
      .ariaLabel('Conflict confirm')
      .ok('Yes')
      .cancel('No')

    return this.$mdDialog.show(confirm)
  }

  viewOpenJobs ($event) {
    let def = this.$q.defer()
    this.Jobs.fetchOpenJobsData()
      .then(res => {
        this.openJobs = res.jobsData
        def.resolve(res.jobsData)
      })
    return def.promise
  }

  favorite (worker) {
    let userIds = worker ? [worker.external_id] : this.Users.getSelected(this.applicantRoute)

    this.Users.favorite(userIds)
      .then((res) => {
        let word = (userIds.length > 1) ? 'users' : 'user'
        const msg = `${userIds.length} ${word} added to favorites!`
        this.toast(msg)
        this.$scope.$broadcast('deselectAll')
        this.updateWorkforce()
      })
      .catch(this.favError)
  }

  unfavorite (worker) {
    let userIds = worker ? [worker.external_id] : this.Users.getSelected(this.applicantRoute)

    this.Users.unfavorite(userIds)
      .then((res) => {
        let word = (userIds.length > 1) ? 'users' : 'user'
        const msg = `${userIds.length} ${word} removed from favorites.`
        this.toast(msg)
        this.$scope.$broadcast('deselectAll')
        this.updateWorkforce()
      })
      .catch(this.unfavError)
  }

  addToCrew (worker) {
    let userIds = worker ? [worker.external_id] : this.Users.getSelected(this.applicantRoute)
    debug('adding the following users to crew: %o', userIds)

    this.selectCrew()
      .then(x => this.Crews.addUsersToCrew(userIds), err => { throw err })
      .then((res) => {
        let word = (userIds.length > 1) ? 'users' : 'user'
        const msg = `${userIds.length} ${word} added to crew.`
        this.toast(msg)
        this.$scope.$broadcast('deselectAll')
      })
      .catch((e) => {
        if (!e) return

        // User clicked manage jobs on add to crew modal.
        if (e === 'manage') {
          this.$state.go('app.crews')
        }
      })
  }

  toggleFacet (name, facetType) {
    // https://github.com/algolia/algoliasearch-helper-js
    this.showFilters = true
    this.filter[facetType] = name
    this.UserSearch.toggleRefinement(facetType, name).search()
    this.UserSearch.on('result', results => {
      this.$scope.$apply(this.facets[facetType] = results.getFacetValues(facetType))
    })
  }

  addNumericFacet (num, facetType, operator) {
    if (!operator) return this.$mdToast.showSimple('Must add operand to numeric facet')
    this.filter[facetType] = facetType + ' ' + operator + num
    this.showFilters = true
    this.UserSearch.addNumericRefinement(facetType, operator, num).search()
  }

  addDateFacet (date, facetType, operator) {
    if (!operator) return this.$mdToast.showSimple('Must add operand to date facet')
    this.filter[facetType] = facetType + ' ' + operator + this.$filter('date')(date, 'shortDate')
    this.showFilters = true
    this.UserSearch.addNumericRefinement(facetType, operator, (new Date(date).getTime() / 1000)).search()
  }

  usersearch () {
    if (this.queryLatLong) {
      this.UserSearch.setQueryParameter('aroundLatLng', this.queryLatLong)
    }
    this.UserSearch.setQuery(this.query).search(this.hitsPerPage)
  }

  nextPage () {
    this.UserSearch.nextPage().search()
  }

  previousPage () {
    this.UserSearch.previousPage().search()
  }

  seeMoreFacets (facetType) {
    this.limit[facetType] = 100
  }

  clearTags () {
    this.showFilters = false
    this.filter = {}
    this.UserSearch.clearRefinements().search()
  }

  removeFilter (type, filter) {
    this.toggleFacet(filter, type)
    this.UserSearch.clearRefinements('ANDFacet').search()
  }

  showGroupMessage ($event) {
    const parentEl = document.body
    return this.$mdDialog.show({
      parent: parentEl,
      targetEvent: $event,
      template: groupMessage,
      locals: {
        users: this.Users.getSelected(this.applicantRoute)
      },
      controller: groupMessageController,
      controllerAs: 'gm'
    })
  }

  singleMessage ($event, workerId) {
    const parentEl = document.body
    return this.$mdDialog.show({
      parent: parentEl,
      targetEvent: $event,
      template: groupMessage,
      locals: {
        users: [workerId]
      },
      controller: groupMessageController,
      controllerAs: 'gm'
    })
  }

  areAnyCMsSelected() {
    return this.workforce.some(worker => worker.selected);
  }
}
